// Firestore document shapes. Server-truth source.
// Client-side types substitute Firebase `Timestamp` for what comes back from the SDK.
//
// Stories are TEMPLATES (immutable catalog entries) that get copied into private
// playable STORY RUNS (instances). Each bot has a single `decision` for the
// scene. Actions are roads taken — no success/failure scoring.
// See docs/game-spec.md.

export type Timestampish = { seconds: number; nanoseconds: number } | Date | number;

// =======================================================================
// Players (spirits)
// =======================================================================

// ---------------- /users/{uid} ----------------
// Server-only written. Auto-bootstrapped via the `bootstrapUser` callable on
// first auth; renamed via `setPlayerName`.
export interface UserDoc {
  schemaVersion: 1;
  displayName: string;
  playerName: string;
  createdAt: Timestampish;
  // ESSENCE — the spirit's personal action budget. Persists across stories.
  // Decremented 1 per whisper and 1 per committed action. Refilled by the
  // `requestEssence` callable (+10 free for MVP; paid later). Initial grant
  // `INITIAL_ESSENCE` is stamped at bootstrap; treat missing as INITIAL_ESSENCE.
  essence: number;
  lastSeenAt: Timestampish;
}

// ---------------- /playerNames/{lower} ----------------
// Reservation index for case-insensitive uniqueness on playerName.
export interface PlayerNameReservationDoc {
  uid: string;
  display: string;
  reservedAt: Timestampish;
}

// =======================================================================
// Stories — templates, runs, scenes, bots, decisions, chat, news.
// All writes go through server callables; clients only read.
// =======================================================================

// Story grouping for the catalog filter. `fiction` is classics + drama;
// `science`/`history` are real figures stuck on a hard problem the player nudges;
// `crime` is famous true-crime moments around one pivotal decision.
export type SceneCategory = 'fiction' | 'science' | 'history' | 'crime';

// Run visibility, chosen at creation.
//   - `public`   — listed on the Public Runs page; any signed-in player can join.
//   - `unlisted` — joinable only by its joinCode / share link.
export type StoryRunVisibility = 'public' | 'unlisted';

// A run's lifecycle. `active` while playing, `complete` once an ending is chosen.
export type StoryRunStatus = 'active' | 'complete';

// ----------------------------------------------------------------------
// Shared sub-shapes (used by both templates and instances)
// ----------------------------------------------------------------------

// An authored ending — a fixed pole. Exactly one per story carries
// `isCanon: true` (how the original really went). The ending DECLARES the
// becomings that define it: `requiredArcs` maps a character key to the arc that
// character must lock for this ending. At the finish, the ending whose required
// arcs are most realized by the locked cast wins (see matchEnding in generate.ts)
// — a strong, explicit arc→ending relation rather than a loose tally.
export interface StoryEnding {
  id: string;            // 'pigs-safe', 'pigs-scattered', ...
  title: string;         // "Snug and Smug"
  summary: string;       // what this ending is + its tone; fed to the generator + recap
  isCanon?: boolean;     // true on the ONE ending that matches the original
  requiredArcs?: Record<string, string>;  // characterKey -> arcId that defines this ending
}

// The original, as it really went. Fed ONLY to the recap call so it can diff the
// player's path against history/the text and mint divergence cards. Absent for
// wholly-original stories — no canon, no divergence cards.
export interface StoryCanon {
  source: string;        // "Shakespeare's Romeo and Juliet (c. 1597)"
  summary: string;       // how the original actually unfolds + its real ending
}

// Story Mountain — the dramatic job of a scene. AUTHORED per scene on the
// template spine (not computed): the author lays out opening → … → resolution and
// the generator renders each beat to its stage. This is what stops scenes
// drifting sideways: each scene has a job (raise pressure / force the turn / land).
export type StoryStage =
  | 'opening'      // the setup — the seed scene
  | 'conflict'     // the central tension breaks open
  | 'build-up'     // rising pressure; arcs still forming
  | 'climax'       // the decisive turn; the last arcs lock here
  | 'aftermath'    // the dust settles; consequences land
  | 'resolution';  // how it finally rests
export const STORY_STAGES: StoryStage[] = [
  'opening', 'conflict', 'build-up', 'climax', 'aftermath', 'resolution',
];

// A possible "who this character becomes" — authored per character (1–4 each).
// Arcs are the heart of the steering: the roads offered to a character are steps
// toward one of their arcs, and once the character's arc LOCKS (at the authored
// `arcLockScene`) the roads only deepen it. Arcs don't name an ending; instead
// each ending declares the arcs it requires (StoryEnding.requiredArcs), and the
// locked cast is matched against those at the finish.
export interface CharacterArc {
  id: string;          // stable per-character ('reckless-romantic')
  title: string;       // "The Reckless Romantic" — shown in the recap + in-play hint
  description: string; // who they become if they walk all the way down this road
}

// A discrete road the player can steer a bot down this scene. No outcome/score —
// just a different direction. `arcId` ties the road to one of THIS character's
// arcs: taking it is a step toward becoming that. The accumulated steps decide
// the character's arc; the locked arcs decide the ending.
export interface ActionOption {
  id: string;            // stable per-scene ('haul-clay', 'straw-and-whistle')
  label: string;         // button text shown to the player
  consequence: string;   // authored narration written into the consequence card
  arcId: string;         // which of this character's arcs this road steps toward
}

// The fork a bot faces this scene — public, shown to the player + given to the
// agent. Rewritten by the generator each scene.
export interface Decision {
  situation: string;        // PUBLIC: the thought/plea floating over the bot
  wish: string;             // PUBLIC: the bot's quiet wish — a steering hint
  fear: string;             // PUBLIC: the bot's quiet fear — a steering hint
  callToAction?: string;    // PUBLIC: optional cold-start framing for a player who hasn't spoken
  actions: ActionOption[];  // PUBLIC: the roads on offer this scene
}

// Snapshot of the road a bot was steered down this scene.
export interface CommittedAction {
  id: string;
  label: string;
  consequence: string;
  arcId: string;            // the arc this road stepped toward (one of the bot's arcs)
  by: string[];             // player names credited with steering this choice
}

// Fields shared by the immutable catalog template and a playable run. Keeps the
// two in lockstep without re-listing every field on both.
export interface StoryCore {
  title: string;             // "Romeo and Juliet"
  category: SceneCategory;
  blurb: string;             // one-liner for the catalog card
  endings: StoryEnding[];    // the authored poles the arcs vote between
  canon?: StoryCanon;        // how the original really went (recap-only)
}

// ---------------- /storyTemplates/{templateId} ----------------
// The catalog. Immutable; authored directly as this doc shape (the seed only
// omits the server-stamped `createdAt`). `startStory` mints a run from it: a
// CAST of characters + an authored SCENE SPINE — scene[0] is the fully-written
// opening, scene[1..n] are skeletons the generator fills in (per their stage +
// instruction) as the run is played.
export interface StoryTemplateDoc extends StoryCore {
  schemaVersion: 1;
  cast: StoryCharacter[];    // the story's characters — identity, stable across scenes
  scenes: TemplateScene[];   // the authored beat spine (length of the story = scenes.length)
  seedKey: string;
  createdAt: Timestampish;
}

// A character's whole-story identity (NOT scene-specific). Referenced from each
// scene by `key`; copied onto every BotDoc minted for that character.
export interface StoryCharacter {
  key: string;               // stable id ('fifer'); becomes seedBotKey on the run's bots
  displayName: string;
  persona: string;
  aspiration: string;
  arcs: CharacterArc[];      // 1–4 possible becomings the roads steer between
  arcLockScene: number;      // scene number by which this character's arc locks
}

// One beat of the authored spine. The opening (scene[0]) is fully authored —
// blurb, description, and each present character's `decision` (the first fork).
// Later beats are SKELETONS: a `name`, a `stage`, and an `instruction` steering
// the generator, which fills in blurb/description and the cast's forks at play time.
export interface TemplateScene {
  name: string;
  stage: StoryStage;         // the scene's Story Mountain job (authored, not computed)
  blurb?: string;            // authored for the opening; generated for skeletons
  description?: string;      // authored atmosphere for the opening; generated for skeletons
  instruction?: string;      // steers the generator when filling a skeleton scene
  bots?: TemplateSceneBot[]; // authored forks (opening); omitted → generator picks cast + writes forks
}

// A character present in an authored scene, with the fork they face. References a
// StoryCharacter by `key`; identity (persona, arcs…) is NOT repeated here.
export interface TemplateSceneBot {
  key: string;               // → StoryCharacter.key
  actionState: BotActionState;
  order?: number;            // turn order within the scene (defaults to DEFAULT_BOT_ORDER)
  decision: Decision;        // the authored fork (roads tag this character's arcs)
}

// ---------------- /stories/{storyId} ----------------
// A RUN (instance). Minted by `startStory` from a template. Private to its
// participants; the play, generation, and trajectory all live here. The run's
// scene docs are ALL minted up front from the authored spine (the opening
// `ready`, the rest blank); story length = the number of scenes.
export interface StoryRunDoc extends StoryCore {
  schemaVersion: 1;
  templateId: string;        // which catalog entry this run was minted from
  ownerUid: string;          // who started it
  participants: string[];    // uids who can play (includes owner); joinStory appends
  visibility: StoryRunVisibility;
  joinCode: string;          // short shareable code for unlisted runs
  status: StoryRunStatus;
  currentSceneId: string;    // the scene currently being played
  sceneCount: number;        // how many scenes are READY/played so far (opening = 1)
  // Running record of the road taken at each completed scene — the story's memory.
  // The per-character arc tallies (and thus the favored ending) are DERIVED from
  // this each generation, so there is no separate steering scoreboard to persist.
  trajectory: TrajectoryScene[];
  // Single-flight generation guard. `true` while the next scene / ending is being
  // produced; the client shows a brief spinner, then the new scene.
  generating: boolean;
  // Set only when status === 'complete'.
  endingId?: string;
  recap?: StoryRecap;
  createdAt: Timestampish;
  updatedAt: Timestampish;
}

// One entry per completed scene, appended when the cast has all decided.
export interface TrajectoryScene {
  sceneName: string;
  bots: TrajectoryBeat[];
}

export interface TrajectoryBeat {
  seedBotKey: string;         // character identity — keys the arc tally
  displayName: string;
  situation: string;          // what the bot faced this scene
  choiceLabel: string;        // the road they were steered down
  choiceConsequence?: string; // what that road actually did (authored narration)
  arcId: string;              // the arc that road stepped toward
  arcTitle?: string;          // that arc's title (readability in prompts + recap)
}

// Initial essence granted at bootstrap (and the fallback for older accounts).
export const INITIAL_ESSENCE = 150;
// Free refill amount when the player taps "request more". Paid later.
export const ESSENCE_REFILL_FREE = 10;

// ---------------- /scenes/{sceneId} ----------------
// ALL of a run's scenes are minted up front from the authored spine. The opening
// is `ready` with its cast; the rest are blanks (`ready: false`, no bots) that the
// generator fills in — blurb/description + the cast's forks — when the page turns,
// then flips `ready: true`. `instruction` is the author's steer for that fill.
export interface SceneDoc {
  schemaVersion: 1;
  name: string;          // "The Capulet Ball" (authored on the spine)
  blurb: string;         // one-liner shown in the scene header (filled for skeletons)
  description: string;   // long atmosphere/setting; fed to the bot's system prompt
  storyId: string;       // back-reference to the parent run /stories/{id}
  storyTitle: string;    // denormalized for header rendering
  order: number;         // 0-based position within the run's scene sequence
  category: SceneCategory;
  stage: StoryStage;     // Story Mountain stage — authored on the spine
  instruction?: string;  // author's steer for the generator when filling this scene
  ready: boolean;        // true once the scene is playable (opening = true; skeletons flip on fill)
  createdAt: Timestampish;
}

// ---------------- /prompts/{id} ----------------
// DEBUG-ONLY. One doc per outgoing LLM call (every dialogue, reaction, scene
// fill, recap, suggestion), so the exact prompt + response can be inspected in
// the Prompts panel. Server-only writes; signed-in read. Wiped on reset.
export interface PromptLogDoc {
  type: 'dialogue' | 'reaction' | 'scene-fill' | 'recap' | 'suggest';
  model: string;
  storyId?: string;       // for filtering to a run
  sceneId?: string;
  botId?: string;
  system?: string;        // the system prompt
  prompt?: string;        // single-prompt calls (generateObject)
  messages?: { role: string; content: string }[]; // multi-turn calls (generateText)
  response?: string;      // the model's output (text, or JSON for structured calls)
  meta?: Record<string, unknown>; // any extra debug info (tool calls, usage…)
  at: Timestampish;
}

// ---------------- /scenes/{sceneId}/players/{uid} ----------------
// Lightweight presence: who is currently in the scene and who they're guiding.
// Server-only-written (heartbeat callable).
export interface ScenePlayerPresenceDoc {
  uid: string;
  playerName: string;
  enteredAt: Timestampish;
  lastSeenAt: Timestampish;
  currentTarget: string | null;   // botId or null
}

// ---------------- /bots/{botId} ----------------
// One row per bot PER SCENE. Each generated scene mints a fresh bot doc (with its
// own chat), so past scenes stay frozen + browsable. Character identity carries
// across scenes via `seedBotKey` ('fifer'), not the doc id. A past scene's bot
// stays `decided` with its `committedAction`; only the current scene's are live.
export interface BotDoc {
  schemaVersion: 1;
  storyId: string;                // the run this bot belongs to
  sceneId: string;                // the scene this bot belongs to (fixed per doc)
  seedBotKey: string;             // stable template key ('fifer') — identity across scenes
  displayName: string;            // "Fifer"
  persona: string;                // short character sheet — fed to the agent
  aspiration: string;             // the bot's big someday-dream — shown to player + agent
  actionState: BotActionState;    // visual idle flavor (🍳, 😴, 🚶...)
  // Character identity (copied from the template; stable across scenes).
  arcs: CharacterArc[];           // the possible becomings the roads steer between
  arcLockScene: number;           // scene number by which this character's arc locks
  // The character's current drift, derived from the run's history when this scene
  // was minted — surfaced as a subtle in-play hint. Absent in the opening scene.
  currentArcId?: string;          // the arc they're leaning toward right now
  arcLocked?: boolean;            // true once arcLockScene has passed — the drift is fixed
  // Sequence index inside the scene. Lower values act first; the player can only
  // interact with a bot once every strictly-lower-ordered bot in the same scene
  // has reached decisionState 'decided'. Bots that share an order act in parallel.
  order: number;
  decisionState: BotDecisionState;  // deciding | decided
  decision: Decision;               // the fork this bot faces this scene
  committedAction?: CommittedAction; // the road taken this scene (set when decided)
  updatedAt: Timestampish;
}

// Default sort bucket for bots whose order is unset.
export const DEFAULT_BOT_ORDER = 999;

// `deciding` = waiting on a committed action. `decided` = a road is locked for
// this scene. When EVERY bot in the scene is `decided`, a participant can turn
// the page with `generateNextScene`.
export type BotDecisionState = 'deciding' | 'decided';

export type BotActionState =
  | 'idle'
  | 'cooking'
  | 'sleeping'
  | 'pacing'
  | 'reading'
  | 'working';

// ---------------- /bots/{botId}/chat/{messageId} ----------------
// Per-bot conversation log. Open to all signed-in readers; server-only writes.
// `kind` discriminates dialogue from the road/consequence/scene events so the UI
// can render them inline.
export type ChatMessageDoc =
  | PlayerChatMessage
  | BotChatMessage
  | ActionChatMessage
  | ConsequenceChatMessage
  | SceneIntroChatMessage;

interface ChatMessageBase {
  at: Timestampish;
  kind: 'player' | 'bot' | 'action' | 'consequence' | 'scene-intro';
}

export interface PlayerChatMessage extends ChatMessageBase {
  kind: 'player';
  playerUid: string;
  playerName: string;
  text: string;
}

export interface BotChatMessage extends ChatMessageBase {
  kind: 'bot';
  text: string;
}

export interface ActionChatMessage extends ChatMessageBase {
  kind: 'action';
  description: string;            // the bot's in-character intent (the road taken)
}

// Plain narration of the road taken — no verdict, no score. Replaces the old
// success/failure/nothing outcome card.
export interface ConsequenceChatMessage extends ChatMessageBase {
  kind: 'consequence';
  consequence: string;            // authored narration
  actionLabel?: string;           // the road's label, for context
}

// An in-stream divider announcing a new scene's situation for this bot.
export interface SceneIntroChatMessage extends ChatMessageBase {
  kind: 'scene-intro';
  sceneName: string;
  order: number;                  // the new scene's 0-based order
  situation: string;              // the bot's opening situation in the new scene
}

// ---------------- /stories/{storyId}/playerChat/{messageId} ----------------
// A run-wide side channel for spirits to coordinate. Anyone in the run can read;
// writes go through `postPlayerChat`.
export interface PlayerChatDoc {
  uid: string;
  playerName: string;
  text: string;
  at: Timestampish;
}

// =======================================================================
// The finish — comprehensive recap + divergence cards.
// =======================================================================

// Structured recap so the client can interleave divergence cards under beats.
export interface StoryRecap {
  title: string;            // a title for THIS run's story
  blurb: string;            // one-line shareable hook
  beats: RecapBeat[];
  closing: string;          // how it all lands, in the player's ending
}

export interface RecapBeat {
  sceneName: string;
  narration: string;
  divergence?: RecapDivergence;  // omitted when the player's beat matched canon
}

export interface RecapDivergence {
  canon: string;            // how the original went here
  mine: string;             // how the player's story went instead
}

// ---------------- /sharedStories/{storyId} ----------------
// World-readable recap mirror — the public, shareable page (route /s/{id}).
// The ONLY collection readable without auth. Server-only writes.
export interface SharedStoryDoc {
  storyTitle: string;       // "Romeo and Juliet"
  category: SceneCategory;
  endingId: string;
  endingTitle: string;
  title: string;            // the run's recap title
  blurb: string;
  beats: RecapBeat[];
  closing: string;
  completedAt: Timestampish;
}

// ---------------- /news/{headlineId} ----------------
// Public feed. Repurposed — fires once when a run reaches an ending.
export interface NewsHeadlineDoc {
  schemaVersion: 1;
  storyId: string;
  storyTitle: string;
  endingId: string;
  endingTitle: string;
  recapTitle: string;
  recapBlurb: string;
  playerNames: string[];    // participants of the run
  at: Timestampish;
}

// =======================================================================
// Bot reply — the NPC's per-turn dialogue. Plain text; structured action
// commits ride on a `commit_action` tool call handled in whisper.ts.
// =======================================================================
export interface BotReply {
  messageToPlayer: string;
}

// =======================================================================
// /system/{flagId} — circuit breaker (unchanged).
// =======================================================================
export interface BreakerDoc {
  tripped: boolean;
  reason?: string;
  dailySpendUsd: number;
  callsLastMinute: number;
  updatedAt: Timestampish;
}
